/**
 * Horizontal bar chart. Each bar has a label + value + max so the same
 * component handles "attempts per dimension" and "accuracy per dimension"
 * without caring about scale.
 *
 * 调用方需保证 max > 0. 当前 (2026/05) 所有调用方都用
 * Math.max(...values, 1) 或者写死 100, 加这条注释提醒未来调用方,
 * 同时 line 40 的 guard 让 max=0 时安全降级到 0% 不画.
 */

interface Bar {
  label: string;
  /** 原始数值 — 决定 bar 的视觉长度 (= value / max). */
  value: number;
  /**
   * Optional. 覆盖 bar 内默认显示的文字 (默认是 value.toLocaleString()).
   * 用于需要带单位/百分号的场景, 比如 "88% 正确" — bar 长度依然按 value 算,
   * 但显示的字符能带语义.
   */
  valueLabel?: string;
  /** 决定 bar 视觉长度的分母. 必须 > 0 (max=0 时 bar 宽 = 0). */
  max: number;
  /** Optional 右侧次要信息 (e.g. "25 题" or "85% 正确"). */
  hint?: string;
}

interface Props {
  bars: readonly Bar[];
  color?: string;
  emptyMsg?: string;
}

export function BarChart({
  bars,
  color = 'rgb(56 189 248)',
  emptyMsg = '暂无数据',
}: Props): JSX.Element {
  const totalActivity = bars.reduce((s, b) => s + b.value, 0);
  if (totalActivity === 0) {
    return (
      <div className="w-full flex items-center justify-center py-6 text-xs text-slate-500">
        {emptyMsg}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {bars.map((b, i) => {
        const pct = b.max > 0 ? Math.min(100, (b.value / b.max) * 100) : 0;
        return (
          <div key={i} className="flex items-center gap-3 text-xs">
            <div className="w-20 shrink-0 text-slate-400">{b.label}</div>
            <div className="flex-1 bg-white/[0.05] rounded h-5 relative overflow-hidden">
              <div
                className="h-full rounded transition-all"
                style={{ width: `${pct.toFixed(1)}%`, backgroundColor: color, opacity: 0.7 }}
              />
              <span className="absolute inset-0 flex items-center px-2 text-[10px] text-slate-100 font-mono">
                {b.valueLabel ?? b.value.toLocaleString()}
                {b.hint !== undefined && (
                  <span className="ml-auto text-slate-300">{b.hint}</span>
                )}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
